/**
 * Records: what is on file, and what happened.
 *
 * The two halves of keeping a record, behind one tab because they answer the
 * same question from opposite ends — "is this aeroplane legal to fly" is read
 * off the shelf, and "why is it grounded" is read out of the log.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useQuery } from '../api.js';
import { useLive } from '../live.js';
import { matchesSearch, SearchField, Segmented } from '../controls.js';
import { Drawer, ErrorNote, Pill, Spinner, Stat, useToast } from '../ui.js';
import { History } from './History.js';
import type { FiledRecord, MissingRecord, RecordKind } from '../../../shared/types.js';

interface Shelf {
  records: FiledRecord[];
  missing: MissingRecord[];
  canManage: boolean;
  seesPeople: boolean;
  summary: { total: number; expired: number; expiring: number; missing: number };
  expiringWindowDays: number;
}

interface Kinds {
  kinds: (RecordKind & { roles: string[] })[];
  expiringWindowDays: number;
}

/** A subject and everything filed against it, plus what is not. */
interface Folder {
  key: string;
  label: string;
  subject: 'aircraft' | 'person';
  aircraftId: number | null;
  userId: number | null;
  records: FiledRecord[];
  missing: MissingRecord[];
  /** Sorts the worst folders to the top: nothing else is worth scrolling for. */
  rank: number;
}

function foldersFrom(shelf: Shelf): Folder[] {
  const map = new Map<string, Folder>();
  const key = (r: { aircraftId: number | null; userId: number | null }) =>
    r.aircraftId != null ? `a${r.aircraftId}` : `u${r.userId}`;

  const folder = (r: FiledRecord | MissingRecord): Folder => {
    const k = key(r);
    let f = map.get(k);
    if (!f) {
      f = {
        key: k, label: r.subjectLabel, subject: r.subject,
        aircraftId: r.aircraftId, userId: r.userId,
        records: [], missing: [], rank: 0,
      };
      map.set(k, f);
    }
    return f;
  };

  for (const r of shelf.records) folder(r).records.push(r);
  for (const m of shelf.missing) folder(m).missing.push(m);

  for (const f of map.values()) {
    f.rank = f.records.filter((r) => r.state === 'Expired').length * 100
      + f.missing.length * 10
      + f.records.filter((r) => r.state === 'Expiring').length;
  }

  return [...map.values()].sort((a, b) =>
    b.rank - a.rank || a.subject.localeCompare(b.subject) || a.label.localeCompare(b.label));
}

export function Records(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'history' ? 'history' : 'files';

  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  return (
    <div className="page">
      <div className="toolbar">
        {/* A div, not a span: the title is a block so the subtitle falls under
            it, and the phone rule that gives the heading its own line targets
            `.toolbar > div`. */}
        <div>
          <div className="toolbar__title">Records</div>
          <span className="toolbar__sub">
            Everything the school holds, and everything it has done.
          </span>
        </div>
        <Segmented
          value={tab}
          onChange={setTab}
          label="Records view"
          options={[
            { value: 'files', label: 'On file' },
            { value: 'history', label: 'Activity' },
          ]}
        />
      </div>

      {tab === 'files' ? <Shelf /> : <ActivityTab />}
    </div>
  );
}

/* ── Activity ─────────────────────────────────────────────────────────── */

/** The log, optionally already narrowed by whatever linked here. */
function ActivityTab(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const entity = params.get('entity') ?? undefined;
  const entityId = params.get('entityId') ? Number(params.get('entityId')) : undefined;

  const clear = () => {
    const p = new URLSearchParams(params);
    p.delete('entity'); p.delete('entityId');
    setParams(p, { replace: true });
  };

  return (
    <div className="card" style={{ padding: '4px 12px 12px' }}>
      {entity && (
        <div className="note" style={{ margin: '10px 0 0' }}>
          Showing one {entity.replace('_', ' ')} only.
          <button className="btn btn--link" onClick={clear}>Show the whole school</button>
        </div>
      )}
      <History entity={entity} entityId={entityId} />
    </div>
  );
}

/* ── On file ──────────────────────────────────────────────────────────── */

function Shelf(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const aircraftId = params.get('aircraft') ? Number(params.get('aircraft')) : undefined;
  const userId = params.get('user') ? Number(params.get('user')) : undefined;

  const path = aircraftId != null ? `/records?aircraftId=${aircraftId}`
    : userId != null ? `/records?userId=${userId}`
    : '/records';

  const { data, error, loading, reload } = useQuery<Shelf>(path);
  const kinds = useQuery<Kinds>('/records/kinds');
  const [scope, setScope] = useState<'all' | 'aircraft' | 'person'>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<FiledRecord | null>(null);
  const [filing, setFiling] = useState<Partial<MissingRecord> | 'new' | null>(null);

  useLive('records', reload);

  const folders = useMemo(() => {
    if (!data) return [];
    return foldersFrom(data)
      .filter((f) => scope === 'all' || f.subject === scope)
      .filter((f) => !q.trim() || matchesSearch(
        q, f.label, ...f.records.map((r) => `${r.kindLabel} ${r.title} ${r.number}`),
      ));
  }, [data, scope, q]);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const narrowed = aircraftId != null || userId != null;

  return (
    <>
      {narrowed && (
        <div className="note" style={{ marginTop: 0 }}>
          Showing one {aircraftId != null ? 'aircraft' : 'person'} only.
          <button className="btn btn--link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            Show the whole cabinet
          </button>
        </div>
      )}

      <div className="stat-grid">
        <Stat label="On file" value={data.summary.total} />
        <Stat label="Expiring" value={data.summary.expiring}
          sub={`within ${data.expiringWindowDays} days`} />
        <Stat label="Expired" value={data.summary.expired} />
        <Stat label="Missing" value={data.summary.missing} sub="required, nothing filed" />
      </div>

      <div className="hist__bar">
        {data.seesPeople && (
          <Segmented
            value={scope}
            onChange={setScope}
            label="Whose records"
            options={[
              { value: 'all', label: 'Everything' },
              { value: 'aircraft', label: 'Aircraft' },
              { value: 'person', label: 'People' },
            ]}
          />
        )}
        <span style={{ flex: 1, minWidth: 160 }}>
          <SearchField value={q} onChange={setQ} label="Search the cabinet"
            placeholder="Tail number, name, document…" />
        </span>
        {data.canManage && (
          <button className="btn btn--primary" onClick={() => setFiling('new')}>File a document</button>
        )}
      </div>

      {folders.length === 0 && (
        <div className="card"><div className="empty">Nothing on file matches that.</div></div>
      )}

      {folders.map((f) => (
        <div className="card" key={f.key} style={{ marginBottom: 12 }}>
          <div className="card__head">
            <span className="card__title">{f.label}</span>
            <span className="card__meta">
              {f.records.length} on file
              {f.missing.length > 0 && ` · ${f.missing.length} missing`}
            </span>
          </div>

          {f.records.map((r) => (
            <button type="button" className="rec" key={r.id} onClick={() => setOpen(r)}>
              <span className="rec__what">
                <span className="rec__title">{r.title}</span>
                <span className="rec__sub">
                  {r.kindLabel}
                  {r.number && ` · ${r.number}`}
                  {r.file && ' · scan attached'}
                </span>
              </span>
              <span className="rec__when">
                {r.expiresOn
                  ? (r.daysLeft! < 0 ? `Expired ${r.expiresOn}` : `To ${r.expiresOn}`)
                  : r.issuedOn ? `Filed ${r.issuedOn}` : ''}
              </span>
              <Pill tone={r.state === 'Expired' ? 'red' : r.state === 'Expiring' ? 'amber'
                : r.state === 'Current' ? 'green' : 'gray'}>
                {r.state}
              </Pill>
            </button>
          ))}

          {f.missing.map((m) => (
            <div className="rec rec--missing" key={`${f.key}-${m.kind}`}>
              <span className="rec__what">
                <span className="rec__title">{m.kindLabel}</span>
                <span className="rec__sub">Nothing on file · {m.reference}</span>
              </span>
              {data.canManage
                ? <button className="btn btn--sm" onClick={() => setFiling(m)}>File it</button>
                : <Pill tone="amber">Missing</Pill>}
            </div>
          ))}
        </div>
      ))}

      {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}

      {open && (
        <RecordDrawer
          record={open}
          canManage={data.canManage}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); reload(); }}
        />
      )}

      {filing && kinds.data && (
        <FileDrawer
          kinds={kinds.data.kinds}
          prefill={filing === 'new' ? null : filing}
          onClose={() => setFiling(null)}
          onFiled={() => { setFiling(null); reload(); }}
        />
      )}
    </>
  );
}

/* ── Embedded ─────────────────────────────────────────────────────────── */

/**
 * One subject's paperwork, for the aircraft drawer and the student record.
 *
 * Deliberately read-only and short. The place to file, correct or supersede a
 * document is the records screen, and there is a way through to it — a drawer
 * that grows a second filing form is a drawer nobody can find anything in.
 */
export function RecordsOnFile({ aircraftId, userId, emptyNote }: {
  aircraftId?: number;
  userId?: number;
  emptyNote?: string;
}): JSX.Element {
  const path = aircraftId != null ? `/records?aircraftId=${aircraftId}`
    : userId != null ? `/records?userId=${userId}` : null;
  const navigate = useNavigate();
  const { data, error, reload } = useQuery<Shelf>(path);
  useLive('records', reload);

  if (error) return <div className="empty">{error}</div>;
  if (!data) return <div className="empty">Reading the file…</div>;
  if (!data.records.length && !data.missing.length) {
    return <div className="empty">{emptyNote ?? 'Nothing on file.'}</div>;
  }

  const href = aircraftId != null ? `/records?aircraft=${aircraftId}` : `/records?user=${userId}`;

  return (
    <>
      {data.records.map((r) => (
        <div className="kv" key={r.id}>
          <span style={{ fontWeight: 400 }}>{r.title}</span>
          <span style={{
            color: r.state === 'Expired' ? 'var(--conflict-c)'
              : r.state === 'Expiring' ? 'var(--sys-orange)' : undefined,
          }}>
            {r.expiresOn
              ? (r.daysLeft! < 0 ? `Expired ${r.expiresOn}` : `To ${r.expiresOn}`)
              : 'On file'}
          </span>
        </div>
      ))}
      {data.missing.map((m) => (
        <div className="kv" key={m.kind}>
          <span style={{ fontWeight: 400 }}>{m.kindLabel}</span>
          <span style={{ color: 'var(--sys-orange)' }}>Nothing on file</span>
        </div>
      ))}
      <div style={{ padding: '10px 24px' }}>
        <button className="btn btn--link" onClick={() => navigate(href)}>Open the file</button>
      </div>
    </>
  );
}

/* ── One record ───────────────────────────────────────────────────────── */

function RecordDrawer({ record, canManage, onClose, onChanged }: {
  record: FiledRecord; canManage: boolean; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [expiresOn, setExpiresOn] = useState(record.expiresOn ?? '');
  const [note, setNote] = useState(record.note);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/records/${record.id}`, {
        expiresOn: expiresOn || null,
        note,
      });
      toast.ok('Record updated');
      onChanged();
    } catch (err) {
      toast.error('Could not save', err instanceof Error ? err.message : undefined);
    } finally { setBusy(false); }
  };

  const supersede = async () => {
    setBusy(true);
    try {
      await api.post(`/records/${record.id}/archive`);
      toast.ok('Superseded', 'It stays in the record; it no longer counts as current.');
      onChanged();
    } catch (err) {
      toast.error('Could not supersede', err instanceof Error ? err.message : undefined);
    } finally { setBusy(false); }
  };

  return (
    <Drawer
      narrow
      title={record.title}
      subtitle={`${record.kindLabel} · ${record.subjectLabel}`}
      onClose={onClose}
      footer={canManage && (
        <span className="row" style={{ gap: 8 }}>
          <button className="btn btn--danger" disabled={busy} onClick={supersede}>Supersede</button>
          <button className="btn btn--primary" disabled={busy} onClick={save}>Save</button>
        </span>
      )}
    >
      <div className="kv"><span>State</span><span><Pill>{record.state}</Pill></span></div>
      {record.number && <div className="kv"><span>Number</span><span>{record.number}</span></div>}
      <div className="kv"><span>Issued</span><span>{record.issuedOn ?? '—'}</span></div>
      <div className="kv">
        <span>Expires</span>
        <span>
          {record.expiresOn
            ? `${record.expiresOn}${record.daysLeft != null
              ? ` · ${record.daysLeft < 0 ? `${Math.abs(record.daysLeft)} days ago` : `in ${record.daysLeft} days`}` : ''}`
            : 'Does not expire'}
        </span>
      </div>
      <div className="kv">
        <span>Filed by</span>
        <span>{record.addedByName} · {new Date(record.addedAt).toLocaleDateString()}</span>
      </div>

      {record.file && (
        <div style={{ padding: '10px 24px' }}>
          {/* Opened in a tab rather than fetched into the page: the browser
              already knows how to show a PDF, and this way it is a real URL
              somebody can send to somebody else. */}
          <a className="btn" href={`/api/records/${record.id}/file`} target="_blank" rel="noreferrer">
            Open {record.file.name} · {Math.round(record.file.size / 1024)} KB
          </a>
        </div>
      )}

      {canManage && (
        <div className="card__body stack">
          <label className="field">
            <span>Expires</span>
            <input className="input" type="date" value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)} />
          </label>
          <label className="field">
            <span>Note</span>
            <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <span className="small muted">
            Superseding keeps the document in the record and stops it counting as current — which
            is what you want when a renewal arrives, and never a reason to delete the old one.
          </span>
        </div>
      )}

      <div style={{ padding: '4px 24px 16px' }}>
        <div className="closeout__h" style={{ marginBottom: 6 }}>What happened to this file</div>
        <History
          compact
          entity={record.aircraftId != null ? 'aircraft' : 'user'}
          entityId={record.aircraftId ?? record.userId ?? undefined}
          emptyNote="Nothing recorded against this file yet."
        />
      </div>
    </Drawer>
  );
}

/* ── Filing a new one ─────────────────────────────────────────────────── */

function FileDrawer({ kinds, prefill, onClose, onFiled }: {
  kinds: (RecordKind & { roles: string[] })[];
  prefill: Partial<MissingRecord> | null;
  onClose: () => void;
  onFiled: () => void;
}): JSX.Element {
  const toast = useToast();
  const subjects = useQuery<{
    aircraft: { id: number; tail: string; model: string }[];
    people: { id: number; name: string; role: string }[];
  }>('/records/subjects');

  const [kindId, setKindId] = useState(prefill?.kind ?? '');
  const [aircraftId, setAircraftId] = useState<number | null>(prefill?.aircraftId ?? null);
  const [userId, setUserId] = useState<number | null>(prefill?.userId ?? null);
  const [title, setTitle] = useState('');
  const [number, setNumber] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<{ name: string; dataUrl: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const kind = kinds.find((k) => k.id === kindId);
  // Picking a kind decides which subject picker to show: a registration is
  // never about a person, and a medical is never about an aeroplane.
  const wantsAircraft = kind?.subject === 'aircraft';

  const pickFile = (input: HTMLInputElement) => {
    const f = input.files?.[0];
    if (!f) return setFile(null);
    if (f.size > 8 * 1024 * 1024) {
      toast.error('That file is larger than 8 MB');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, dataUrl: String(reader.result) });
    reader.readAsDataURL(f);
  };

  const ready = !!kind && (wantsAircraft ? aircraftId != null : userId != null)
    && (!kind.expires || !!expiresOn);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/records', {
        kind: kindId,
        aircraftId: wantsAircraft ? aircraftId : null,
        userId: wantsAircraft ? null : userId,
        title: title.trim() || undefined,
        number: number.trim() || undefined,
        issuedOn: issuedOn || null,
        expiresOn: expiresOn || null,
        note: note.trim() || undefined,
        file,
      });
      toast.ok('Filed', 'It is on the shelf and in the record.');
      onFiled();
    } catch (err) {
      toast.error('Could not file it', err instanceof Error ? err.message : undefined);
    } finally { setBusy(false); }
  };

  return (
    <Drawer
      narrow
      title="File a document"
      subtitle={prefill?.subjectLabel ? `For ${prefill.subjectLabel}` : undefined}
      onClose={onClose}
      footer={<button className="btn btn--primary" disabled={!ready || busy} onClick={save}>File it</button>}
    >
      <div className="card__body stack">
        <label className="field">
          <span>Kind</span>
          <select className="select" value={kindId} onChange={(e) => {
            setKindId(e.target.value);
            // Switching between an aircraft kind and a person kind leaves the
            // other picker holding a subject that no longer applies.
            const next = kinds.find((k) => k.id === e.target.value);
            if (next?.subject === 'aircraft') setUserId(null); else setAircraftId(null);
          }}>
            <option value="">Choose…</option>
            <optgroup label="Aircraft">
              {kinds.filter((k) => k.subject === 'aircraft').map((k) => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </optgroup>
            <optgroup label="People">
              {kinds.filter((k) => k.subject === 'person').map((k) => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </optgroup>
          </select>
        </label>

        {kind && (
          <div className="note" style={{ margin: 0 }}>
            <strong>{kind.reference}</strong> — {kind.note}
          </div>
        )}

        {kind && wantsAircraft && (
          <label className="field">
            <span>Aircraft</span>
            <select className="select" value={aircraftId ?? ''}
              onChange={(e) => setAircraftId(Number(e.target.value) || null)}>
              <option value="">Choose…</option>
              {subjects.data?.aircraft.map((a) => (
                <option key={a.id} value={a.id}>{a.tail} · {a.model}</option>
              ))}
            </select>
          </label>
        )}

        {kind && !wantsAircraft && (
          <label className="field">
            <span>Person</span>
            <select className="select" value={userId ?? ''}
              onChange={(e) => setUserId(Number(e.target.value) || null)}>
              <option value="">Choose…</option>
              {subjects.data?.people.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.role}</option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span>Title</span>
          <input className="input" value={title} placeholder={kind?.label ?? ''}
            onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span>Number</span>
          <input className="input" value={number} placeholder="Certificate or policy number"
            onChange={(e) => setNumber(e.target.value)} />
        </label>
        <label className="field">
          <span>Issued</span>
          <input className="input" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
        </label>
        <label className="field">
          <span>Expires{kind?.expires ? '' : ' (optional)'}</span>
          <input className="input" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </label>
        {kind?.expires && !expiresOn && (
          <span className="small" style={{ color: 'var(--sys-red)' }}>
            A {kind.label.toLowerCase()} is only meaningful with the date it runs out.
          </span>
        )}
        <label className="field">
          <span>Scan</span>
          <input className="input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => pickFile(e.currentTarget)} />
        </label>
        <label className="field">
          <span>Note</span>
          <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
    </Drawer>
  );
}
